<template>
  <div class="kadar-respon2">
    <v-snackbar
      :timeout="snackbar.timeout"
      :color="snackbar.color"
      v-model="snackbar.show"
    >
      {{ snackbar.message }}
    </v-snackbar>

    <PageHeader
      icon="mdi-chart-donut-variant"
      title="Kadar Respon Isi Rumah"
      subtitle="Kadar respon mengikut negeri, strata dan daerah pentadbiran"
    />

    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-2"></v-progress-linear>

    <!-- Filters -->
    <v-card outlined class="mb-4">
      <!-- Setiap penapis memakai susunan yang SAMA: label eyebrow di atas,
           kawalan di bawah. Sebelum ini Negeri/Daerah memakai label terapung
           manakala Pusingan/Sampel memakai kapsyen di sebelah kiri — dua cara
           melabel dalam satu bar. `align="end"` menjajarkan semua kawalan pada
           garis dasar yang sama walaupun tinggi labelnya berbeza. -->
      <v-card-text class="filter-bar py-3">
        <v-row align="end" dense>
          <v-col cols="12" sm="3">
            <div class="fld__label">Negeri</div>
            <v-autocomplete
              v-model="negeri"
              :items="negeriList"
              :disabled="negeriLocked"
              outlined dense hide-details
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="fld__label">Daerah Pentadbiran</div>
            <!-- Tiada pilihan = tiada tapisan daerah (semua). Label eyebrow di
                 atas menggantikan label terapung, jadi medan kosong akan
                 kelihatan KOSONG tanpa placeholder ini. -->
            <v-autocomplete
              v-model="daerah"
              :items="daerahSelectItems"
              :disabled="negeri === 'ALL'"
              placeholder="Semua daerah"
              multiple outlined dense hide-details clearable
            >
              <template v-slot:selection="{ index }">
                <span v-if="index === 0" class="caption text--secondary">{{ daerahLabel }}</span>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col cols="12" sm="auto">
            <div class="fld__label">Pusingan</div>
            <div class="d-flex align-center pusingan-range">
              <v-select
                v-model="roundFrom"
                :items="roundFromItems"
                outlined dense hide-details
                class="pusingan-sel"
              ></v-select>
              <span class="pusingan-dash mx-1">–</span>
              <v-select
                v-model="roundTo"
                :items="roundToItems"
                outlined dense hide-details
                class="pusingan-sel"
              ></v-select>
            </div>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="fld__label">Sampel</div>
            <!-- `color="primary"` digugurkan: primary Vuetify = biru, yang
                 bertentangan dengan sistem teal. Keadaan aktif diwarnakan
                 melalui CSS (.data-toggle) sebaliknya. -->
            <!-- 38, bukan 40: + birai 1px atas/bawah = tepat 40px, sama tinggi
                 dengan medan outlined di sebelahnya, supaya label "SAMPEL" duduk
                 pada garis yang sama dengan "NEGERI"/"PUSINGAN". -->
            <v-btn-toggle v-model="sampel" mandatory class="data-toggle">
              <v-btn value="1" height="38" class="text-none px-4">1</v-btn>
              <v-btn value="2" height="38" class="text-none px-4">2</v-btn>
              <v-btn value="3" height="38" class="text-none px-4">Semua</v-btn>
            </v-btn-toggle>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="12" sm="auto">
            <v-btn outlined height="40" class="text-none reset-btn" @click="resetFilters">
              <v-icon small left>mdi-refresh</v-icon>
              Tetapkan Semula
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Overview: donut + state bars -->
    <v-row class="mb-4" align="stretch">
      <v-col cols="12" md="4" class="d-flex">
        <v-card outlined class="d-flex flex-column flex-grow-1" :class="{ 'fullscreen-card': isFullscreen }">
          <v-card-title class="py-2 subtitle-2">
            <span class="mr-2" :style="{ color: T.graf }">&#9679;</span>
            Kadar Respon Keseluruhan
            <v-spacer></v-spacer>
            <!-- <span class="caption grey--text">{{ scopeName }}</span> -->
            <span class="penapis-ringkas grey--text">{{ filterSummary }}</span>
            <!-- <v-btn icon small class="ml-2" @click="toggleFullscreen">
              <v-icon>{{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
            </v-btn> -->
          </v-card-title>
          <v-card-text class="chart-container" :class="{ 'fullscreen-chart': isFullscreen }">
            <canvas ref="donut"></canvas>
            <div class="donut-center">
              <div class="donut-pct" :style="{ color: COL.navy }"><animated-number :value="scope.pctSelesai" :format="pf" /></div>
              <div class="donut-lab text--secondary">Selesai</div>
            </div>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-text class="py-3 flex-grow-1 kpi-wrap">
            <v-row dense class="kpi-row">
              <v-col cols="6" v-for="(card, i) in summaryCards" :key="i" class="d-flex">
                <div class="kpi-tile flex-grow-1 d-flex flex-column justify-center">
                  <div class="kpi-v" :style="{ color: card.color || '' }"><animated-number :value="card.value" :format="card.format" /></div>
                  <div class="kpi-l text--secondary">{{ card.label }}</div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="8" class="d-flex">
        <v-card outlined class="d-flex flex-column flex-grow-1">
          <v-card-title class="py-2 subtitle-2">
            <v-icon small class="mr-2">mdi-chart-bar</v-icon>
            Kadar Respon Mengikut Negeri
            <v-spacer></v-spacer>
            <div class="legend">
              <span><span class="dot" :style="{ background: T.graf }"></span>≥ 90%</span>
              <span><span class="dot" :style="{ background: COL.amber }"></span>85–89%</span>
              <span><span class="dot" :style="{ background: COL.red }"></span>&lt; 85%</span>
            </div>
            <v-btn-toggle v-model="sortBy" mandatory dense class="ml-2 sort-toggle" title="Susunan">
              <v-btn value="kod" class="text-none px-2">
                <v-icon small class="mr-1">mdi-sort-numeric-ascending</v-icon>Kod
              </v-btn>
              <v-btn
                value="pct" class="text-none px-2"
                :title="pctDesc ? 'Menurun — klik untuk menaik' : 'Menaik — klik untuk menurun'"
                @mousedown="notePctState" @click="togglePctDir"
              >
                <v-icon small class="mr-1">{{ pctDesc ? 'mdi-sort-descending' : 'mdi-sort-ascending' }}</v-icon>Peratus
              </v-btn>
            </v-btn-toggle>
            <v-btn-toggle v-model="negeriView" mandatory dense class="ml-2 view-toggle">
              <v-btn value="chart" title="Graf bar">
                <v-icon>mdi-chart-bar</v-icon>
              </v-btn>
              <v-btn value="bars" title="Senarai bar kemajuan">
                <v-icon>mdi-format-list-bulleted</v-icon>
              </v-btn>
            </v-btn-toggle>
            <v-menu v-if="negeriView === 'chart'" offset-y>
              <template v-slot:activator="{ on, attrs }">
                <!-- Saiz lalai (medium), sepadan dengan dua toggle di
                     sebelahnya: butang ikon lalai 36px = tinggi butang toggle
                     lalai, jadi ketiga-tiganya sebaris tanpa tinggi tetap. -->
                <v-btn icon class="ml-2 dl-btn" title="Muat turun graf" v-bind="attrs" v-on="on">
                  <v-icon>mdi-download</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item @click="downloadNegeriChart('png')">
                  <v-list-item-icon class="mr-2"><v-icon small>mdi-file-image-outline</v-icon></v-list-item-icon>
                  <v-list-item-title>PNG</v-list-item-title>
                </v-list-item>
                <v-list-item @click="downloadNegeriChart('jpg')">
                  <v-list-item-icon class="mr-2"><v-icon small>mdi-file-image</v-icon></v-list-item-icon>
                  <v-list-item-title>JPG</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-card-title>
          <v-card-text class="py-2 pb-0">
            <transition-group
              v-if="negeriView === 'bars'"
              name="sbar-flip"
              tag="div"
              class="sbar-cols"
              :class="{ revealing: revealPlaying }"
              :style="{ '--bar-rows': Math.ceil(chartRows.length / 2) }"
            >
              <div
                v-for="(r, i) in chartRows"
                :key="r.ng"
                class="sbar"
                :class="{ sel: negeri !== 'ALL' && negeri === r.ng }"
                :style="{ '--i': i }"
                :title="`${r.state}: ${fmt(r.selesai)}/${fmt(r.jumlah)} selesai`"
                @click="!negeriLocked && (negeri = negeri === r.ng ? 'ALL' : r.ng)"
              >
                <div class="d-flex justify-space-between mb-1">
                  <span class="sbar-lab font-weight-bold">{{ r.state }}</span>
                  <span class="sbar-lab font-weight-bold" :style="{ color: pctColor(r.pctSelesai) }"><animated-number :value="r.pctSelesai" :format="pf" /></span>
                </div>
                <v-progress-linear
                  :value="barsRevealed ? r.pctSelesai : 0"
                  :color="pctColor(r.pctSelesai)"
                  height="16"
                  rounded
                  background-color="grey lighten-3"
                >
                  <span class="sbar-count">
                    <span class="font-weight-bold"><animated-number :value="r.selesai" :format="fmt" /></span>&nbsp;/&nbsp;<animated-number :value="r.jumlah" :format="fmt" />
                  </span>
                </v-progress-linear>
              </div>
            </transition-group>
            <div
              v-else
              class="negeri-chart-box"
              :style="{ position: 'relative', height: negeriChartHeight + 'px' }"
            >
              <canvas ref="negeriChart"></canvas>
            </div>
            <div class="nota-skop text--secondary d-flex align-start mt-2 mb-2">
              <v-icon small class="mr-1 mt-1">mdi-information-outline</v-icon>
              <span v-if="negeriLocked">
                Bahagian ini memaparkan peratus kadar respon penuh bagi negeri anda sahaja.
                Penapis <strong>Pusingan</strong> dan <strong>Sampel</strong> mempengaruhi nilai di sini.
              </span>
              <span v-else>
                Bahagian ini sentiasa memaparkan peratus kadar respon penuh bagi setiap negeri.
                Hanya penapis <strong>Pusingan</strong> dan <strong>Sampel</strong> mempengaruhi nilai di sini —
                penapis <strong>Negeri</strong> dan <strong>Daerah Pentadbiran</strong> tidak memberi kesan.
              </span>

            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Kadar respon mengikut daerah pentadbiran — appears when a negeri is selected -->
    <v-card v-if="negeri !== 'ALL' && daerahRows.length" outlined class="mb-4">
      <v-card-title class="py-2 subtitle-2">
        <v-icon small class="mr-2">mdi-map-marker-radius-outline</v-icon>
        Kadar Respon Mengikut Daerah Pentadbiran
        <v-chip x-small color="grey lighten-3" class="ml-3 font-weight-medium">{{ negeriName[negeri] || negeri }}</v-chip>
        <v-spacer></v-spacer>
        <div class="legend">
          <span><span class="dot" :style="{ background: T.graf }"></span>≥ 90%</span>
          <span><span class="dot" :style="{ background: COL.amber }"></span>85–89%</span>
          <span><span class="dot" :style="{ background: COL.red }"></span>&lt; 85%</span>
        </div>
        <v-btn-toggle v-model="sortBy" mandatory dense class="ml-2 sort-toggle" title="Susunan">
          <v-btn value="kod" class="text-none px-2">
            <v-icon small class="mr-1">mdi-sort-numeric-ascending</v-icon>Kod
          </v-btn>
          <v-btn
            value="pct" class="text-none px-2"
            :title="pctDesc ? 'Menurun — klik untuk menaik' : 'Menaik — klik untuk menurun'"
            @mousedown="notePctState" @click="togglePctDir"
          >
            <v-icon small class="mr-1">{{ pctDesc ? 'mdi-sort-descending' : 'mdi-sort-ascending' }}</v-icon>Peratus
          </v-btn>
        </v-btn-toggle>
        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon class="ml-2 dl-btn" title="Muat turun graf" v-bind="attrs" v-on="on">
              <v-icon>mdi-download</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item @click="downloadDaerahChart('png')">
              <v-list-item-icon class="mr-2"><v-icon small>mdi-file-image-outline</v-icon></v-list-item-icon>
              <v-list-item-title>PNG</v-list-item-title>
            </v-list-item>
            <v-list-item @click="downloadDaerahChart('jpg')">
              <v-list-item-icon class="mr-2"><v-icon small>mdi-file-image</v-icon></v-list-item-icon>
              <v-list-item-title>JPG</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-card-title>
      <v-card-text class="py-2">
        <div :style="{ position: 'relative', height: daerahChartHeight + 'px' }">
          <canvas ref="daerahChart"></canvas>
        </div>
      </v-card-text>
    </v-card>

    <!-- Detailed table -->
    <v-card outlined class="mb-4">
      <v-card-title class="py-3 d-flex align-center">
        <v-icon small class="mr-2">mdi-table</v-icon>
        <span class="subtitle-1 font-weight-medium">Jadual Terperinci Kadar Respon</span>
        <v-chip x-small color="grey lighten-3" class="ml-3 font-weight-medium">{{ tableRows.length }} negeri</v-chip>
        <v-spacer></v-spacer>
        <v-btn color="green darken-2" dark class="mr-2 text-none" :loading="exporting" @click="exportExcel">
          <v-icon left>mdi-microsoft-excel</v-icon>
          Excel
        </v-btn>
        <v-btn color="red darken-1" dark class="text-none" :loading="exportingPdf" @click="exportPDF">
          <v-icon left>mdi-file-pdf-box</v-icon>
          PDF
        </v-btn>
      </v-card-title>

      <v-card-text class="d-flex align-center flex-wrap py-2">
        <!-- <span class="subtitle-2 font-weight-medium">{{ scopeName }}</span> -->
        <v-spacer></v-spacer>
        <span class="penapis-ringkas text--secondary">{{ filterSummary }}</span>
      </v-card-text>

      <v-simple-table class="kr2-table">
        <thead>
          <tr>
            <th class="cat" rowspan="2">Kategori</th>
            <th rowspan="2">Jumlah<br>Tempat<br>Kediaman</th>
            <th rowspan="2">Tempat<br>Kediaman<br>Selesai (1)</th>
            <th colspan="7">Tiada Sambutan</th>
            <th colspan="2">Tiada Sambutan TSD &amp; EKS</th>
            <th rowspan="2">%<br>Selesai</th>
            <th rowspan="2">% Selesai<br>Selain<br>TSD &amp; EKS</th>
          </tr>
          <tr>
            <th>Kosong<br>(2)</th>
            <th>Rumah Dlm<br>Pembinaan (4)</th>
            <th>Bukan TK /<br>TK Institusi (5)</th>
            <th>Roboh /<br>Usang (6)</th>
            <th>Tempat<br>Layak (7)</th>
            <th>Lain-lain<br>(9)</th>
            <th>Lain-lain<br>Didiami (0)</th>
            <th>Tiada Siapa<br>Di Rumah (3)</th>
            <th>Enggan<br>Kerjasama (8)</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="r in visibleRows">
          <tr v-if="r._isGroup" :key="r.state + '-g'" class="group">
            <td class="cat" colspan="14">{{ r.state }}</td>
          </tr>
          <tr v-else-if="r._isSpacer" :key="r.state + '-s'" class="spacer">
            <td colspan="14"></td>
          </tr>
          <tr v-else :key="r.state" :class="{ total: r._isTotal, strata: r._isStrata, daerah: r._isDaerah }">
            <td class="cat">{{ r.state }}</td>
            <td>{{ fmt(r.jumlah) }}</td>
            <td>{{ fmt(r.selesai) }}</td>
            <td>{{ fmt(r.kosong) }}</td><td>{{ fmt(r.pembinaan) }}</td><td>{{ fmt(r.bukan) }}</td>
            <td>{{ fmt(r.roboh) }}</td><td>{{ fmt(r.layak) }}</td><td>{{ fmt(r.lain9) }}</td>
            <td>{{ fmt(r.lain0) }}</td><td>{{ fmt(r.tiada3) }}</td><td>{{ fmt(r.enggan8) }}</td>
            <td>
              <span v-if="r._isTotal" class="font-weight-bold">{{ pfn(r.pctSelesai) }}</span>
              <span v-else class="pill" :style="{ background: pctColor(r.pctSelesai) }">{{ pfn(r.pctSelesai) }}</span>
            </td>
            <td>{{ pfn(r.pctSelain) }}</td>
          </tr>
          </template>
          <tr v-if="!visibleRows.length">
            <td colspan="14" class="text-center grey--text" style="padding:24px">Tiada rekod dijumpai</td>
          </tr>
        </tbody>
      </v-simple-table>

      <div class="foot penapis-ringkas text--secondary px-4 py-3 d-flex justify-space-between flex-wrap">
        <!-- <span>Memaparkan {{ stateCount }} negeri{{ hasTotal ? ' + jumlah Malaysia' : '' }}</span> -->

        <span></span>
        <span>{{ filterSummary }}</span>
      </div>
    </v-card>
  </div>
</template>

<script>
import Chart from "chart.js";
import axios from "@/plugins/axios";
import hiesWatermark from "@/assets/HIES watermark.png";
import getPusingan from "@/services/pusingan";
import { negeriLock } from "@/services/negeriScope";
import { surveyTitle } from "@/services/surveyYear";
import { addJataHeaderExcel, addJataHeaderPdf } from "@/services/exportHeader";
import { T, loadTheme, argb, rgb, dark } from "@/services/theme";

const pusinganSvc = new getPusingan();

/* ============ DATA MODEL (fed by /JadualAwalan/getKadarResponJR2) ============ */
// "Tiada Sambutan" category keys (everything except Selesai). Order matches the table.
const COMP = ["kosong","pembinaan","bukan","roboh","layak","lain9","lain0","tiada3","enggan8"];
// "Selain TSD & EKS" group — the 7 plain Tiada Sambutan codes (everything
// except tiada3 + enggan8). pctSelain = sum(SELAIN_TSD_EKS) / jumlah × 100.
const SELAIN_TSD_EKS = ["kosong","pembinaan","bukan","roboh","layak","lain9","lain0"];

// Turn one aggregated API row ({ jumlah, selesai, kosong, ... }) into the row
// shape the table/computeds expect (adds tsdeks, tiadaSambutan, pct*).
function shapeRow(api, label) {
  const r = { state: label, jumlah: +api.jumlah || 0, selesai: +api.selesai || 0 };
  COMP.forEach(k => (r[k] = +api[k] || 0));
  r.tsdeks = SELAIN_TSD_EKS.reduce((a, k) => a + r[k], 0);
  r.tiadaSambutan = r.jumlah - r.selesai;
  r.pctSelesai = r.jumlah ? (r.selesai / r.jumlah) * 100 : 0;
  r.pctSelain = r.jumlah ? (r.tsdeks / r.jumlah) * 100 : 0;
  return r;
}

// navy = donut/bar ink, amber+red = status thresholds. All fixed: they encode
// meaning, not branding. The accent lives in the theme (T.graf).
const COL = { navy: "#0B1F3A", amber: "#E0A800", red: "#D9534F" };

// '#RRGGBB' → 'rgba(r,g,b,a)' — used to fade the non-selected bars in the graph view
function hexA(hex, a) {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

// Chart.js v2 plugin for the negeri bar-graph view: prints each bar's % just
// past its end plus the selesai/jumlah counts on the bar. Both labels ANIMATE
// with the bars: the % is derived from the bar's current (tweened) pixel length,
// and the counts lerp from their previous values using the chart's animation
// progress (chart._labelProgress, driven by the animation callbacks in
// drawNegeriChart), so sorting / new data rolls the numbers instead of snapping.
const fmtMY = new Intl.NumberFormat("en-MY");
const pctLabelPlugin = {
  // selected negeri's bar renders thicker (ds._selIndex). Set as an ABSOLUTE
  // height each frame — v2 recomputes/reuses the view, so a multiplier would
  // compound across hover re-renders.
  beforeDatasetsDraw(chart) {
    const ds = chart.data.datasets[0];
    if (ds._selIndex == null) return;
    const el = chart.getDatasetMeta(0).data[ds._selIndex];
    if (el && el._view) el._view.height = 30;
  },
  afterDatasetsDraw(chart) {
    const ctx = chart.ctx;
    const meta = chart.getDatasetMeta(0);
    const ds = chart.data.datasets[0];
    const xScale = chart.scales["x-axis-0"];
    const P = chart._labelProgress == null ? 1 : Math.min(1, chart._labelProgress);
    const Pe = 1 - Math.pow(1 - P, 4);   // easeOutQuart — matches the bar tween
    // px, bukan rem: teks ini dilukis terus ke <canvas> melalui ctx.font, yang
    // tidak mengenal rem mahupun mewarisi CSS. Nilainya dipadankan dengan tangga
    // taip secara manual — 13px = --fs-xs, 12px = --fs-2xs.
    const PCT_FONT = "600 13px 'Roboto', sans-serif";
    const CNT_FONT = "500 12px 'Roboto', sans-serif";
    ctx.save();
    ctx.textBaseline = "middle";
    meta.data.forEach((el, i) => {
      const v = ds.data[i];
      if (v == null || !el._model) return;
      const m = el._model;
      // % follows the bar's animated length, counting as it moves
      const shown = xScale
        ? Math.max(0, Math.min(100, xScale.getValueForPixel(m.x)))
        : v;
      const pct = shown.toFixed(1) + "%";
      ctx.font = PCT_FONT;
      ctx.textAlign = "left";
      ctx.fillStyle = "#334155";
      ctx.fillText(pct, m.x + 5, m.y);
      // counts roll old → new alongside the same animation
      const cur = ds._counts && ds._counts[i];
      if (!cur) return;
      const prev = (ds._prevCounts && ds._prevCounts[i]) || [0, 0];
      const count =
        fmtMY.format(Math.round(prev[0] + (cur[0] - prev[0]) * Pe)) + "/" +
        fmtMY.format(Math.round(prev[1] + (cur[1] - prev[1]) * Pe));
      const pctW = ctx.measureText(pct).width;
      ctx.font = CNT_FONT;
      const cntW = ctx.measureText(count).width;
      if (Math.abs(m.x - m.base) >= cntW + 14) {
        ctx.textAlign = "right";
        // faded (non-selected) bars are too light for white text — use dark ink
        ctx.fillStyle = ds._muted && ds._muted[i] ? "rgba(51,65,85,.6)" : "rgba(255,255,255,.95)";
        ctx.fillText(count, m.x - 7, m.y);
      } else {
        ctx.textAlign = "left";
        ctx.fillStyle = "#64748B";
        ctx.fillText(count, m.x + 5 + pctW + 6, m.y);
      }
    });
    ctx.restore();
  },
};

// <animated-number :value="n" :format="fmt" /> — renders format(display) and
// tweens display towards value (easeOutQuart, paced like the donut sweep) on
// mount (0 → value) and whenever value changes (filter/reload).
const AnimatedNumber = {
  props: { value: { type: Number, default: 0 }, format: { type: Function, default: v => v } },
  data: () => ({ display: 0 }),
  watch: {
    value(to) { this.tween(+to || 0); },
  },
  mounted() { this.tween(+this.value || 0); },
  beforeDestroy() { cancelAnimationFrame(this._raf); },
  methods: {
    tween(to) {
      cancelAnimationFrame(this._raf);
      const from = this.display;
      if (from === to) return;
      const start = performance.now(), dur = 1200;
      const step = now => {
        const t = Math.min((now - start) / dur, 1);
        this.display = t < 1 ? from + (to - from) * (1 - Math.pow(1 - t, 4)) : to;
        if (t < 1) this._raf = requestAnimationFrame(step);
      };
      this._raf = requestAnimationFrame(step);
    },
  },
  render(h) { return h("span", this.format(this.display)); },
};

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if ([...document.scripts].some(s => s.src === src)) return resolve();
    const el = document.createElement("script");
    el.src = src;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error("Gagal memuatkan " + src));
    document.head.appendChild(el);
  });
}

// load an image asset → { dataUrl(PNG base64), width, height }
// alpha (0-1, optional): bake transparency into the pixels — used for watermarks,
// since jsPDF's addImage has no opacity option of its own
function loadImage(url, alpha) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const c2d = canvas.getContext("2d");
      if (alpha != null) c2d.globalAlpha = alpha;
      c2d.drawImage(img, 0, 0);
      let dataUrl;
      try { dataUrl = canvas.toDataURL("image/png"); } catch (e) { dataUrl = url; }
      resolve({ dataUrl, width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = reject;
    img.src = url;
  });
}

export default {
  name: "Kadarrespon",
  components: { AnimatedNumber },
  data() {
    return {
      COL,
      T,  // configurable palette — templates bind T.graf; repaints when it loads
      snackbar: { show: false, message: "", color: "success", timeout: 3000 },
      negeri: "ALL",                 // selected negeriID ("01".."16") or "ALL"
      negeriLocked: false,           // true for PP/PO → negeri pinned to own kodneg
      negeriView: "chart",           // negeri card display: "chart" (bar graph, default) | "bars" (progress list)
      barsRevealed: false,           // page-enter: bars fill 0 → value once data lands
      revealPlaying: true,           // slow staggered fill active (page enter only)
      sortBy: "kod",                 // chart bar order: "kod" (negeri/daerah code, default) | "pct"
      pctDesc: true,                 // Peratus direction: true = highest first; re-click the button to flip
      daerah: [],                    // selected DP codes (e.g. ["03","04"])
      sampel: "3",                   // sample filter: "1" / "2" / "3" (Semua)
      search: "",
      // Active pusingan only — loaded in mounted() from getAllPusingan() filtered
      // by status == 1 (same rule as the Wajaran / Tapis Pusingan view). Falls back
      // to 1..12 if the service is unavailable.
      roundItems: [],
      // Pusingan range filter (From–To). Defaults to first–last available in
      // loadActiveRounds(). selectedRoundsModel is derived from this range.
      roundFrom: null,
      roundTo: null,
      donutChart: null,
      isFullscreen: false,
      exporting: false,
      exportingPdf: false,
      loading: false,
      // API data
      negeriLookup: [],   // [{ id, nama }] from lookupnegeri (built from perNegeri)
      apiNegeri: [],      // per-negeri aggregated rows
      apiStrata: [],      // [{ strata, ...counts }] for selected negeri
      apiDaerah: [],      // [{ dp, namaDP, ...counts }] for selected negeri (table rows)
      apiDaerahList: [],  // [{ dp, namaDP }] all districts of the negeri (dropdown)
      apiScopeTotal: null,// DP-aware total for the selected negeri (total row/donut)
    };
  },
  computed: {
    todayStr() {
      return new Date().toISOString().slice(0, 10);
    },
    negeriList() {
      // Locked roles (PP/PO) only see their own negeri — no Malaysia/other options.
      if (this.negeriLocked) {
        return this.negeriLookup
          .filter(n => n.id === this.negeri)
          .map(n => ({ text: n.nama, value: n.id }));
      }
      return [
        { text: "Malaysia", value: "ALL" },
        ...this.negeriLookup.map(n => ({ text: n.nama, value: n.id })),
      ];
    },
    // The selected pusingan = every available round within the From–To range.
    // Derived from roundFrom/roundTo so the rest of the page (fetch param,
    // summary, exports) keeps working unchanged.
    selectedRoundsModel() {
      if (this.roundFrom == null || this.roundTo == null) return [];
      const lo = Math.min(this.roundFrom, this.roundTo);
      const hi = Math.max(this.roundFrom, this.roundTo);
      return this.roundItems.filter(p => p >= lo && p <= hi);
    },
    selectedRounds() {
      return [...this.selectedRoundsModel].sort((a, b) => a - b);
    },
    allRounds() {
      return this.roundItems.length > 0 && this.selectedRoundsModel.length === this.roundItems.length;
    },
    // "From" can be any available round; "To" only rounds >= the chosen From.
    roundFromItems() {
      return this.roundItems.map(p => ({ text: String(p).padStart(2, "0"), value: p }));
    },
    roundToItems() {
      const lo = this.roundFrom == null ? -Infinity : this.roundFrom;
      return this.roundItems.filter(p => p >= lo).map(p => ({ text: String(p).padStart(2, "0"), value: p }));
    },
    ddLabel() {
      if (this.allRounds) return "Semua Pusingan";
      if (!this.selectedRoundsModel.length) return "Tiada dipilih";
      const lo = this.selectedRounds[0];
      const hi = this.selectedRounds[this.selectedRounds.length - 1];
      return lo === hi ? `Pusingan ${lo}` : `Pusingan ${lo}–${hi}`;
    },
    // map negeriID → nama (for labels)
    negeriName() {
      const map = {};
      this.negeriLookup.forEach(n => (map[n.id] = n.nama));
      return map;
    },
    // dependent daerah dropdown items (from API daerahList for the selected negeri)
    daerahSelectItems() {
      return this.apiDaerahList.map(d => ({ text: d.namaDP || d.dp, value: d.dp }));
    },
    daerahName() {
      const map = {};
      this.apiDaerahList.forEach(d => (map[d.dp] = d.namaDP || d.dp));
      return map;
    },
    // compact label for the daerah multi-select chip area
    daerahLabel() {
      const n = this.daerah.length;
      if (n === 0) return "";
      if (n === 1) return this.daerahName[this.daerah[0]] || this.daerah[0];
      if (n === this.daerahSelectItems.length) return "Semua Daerah";
      return `${n} daerah dipilih`;
    },
    allRows() {
      // index API rows by negeri id
      const byNg = {};
      this.apiNegeri.forEach(r => (byNg[r.ng] = r));
      // always render a row for every negeri in the lookup, so the chart bars
      // stay visible (at 0%) even when the API returns no data (e.g. Sampel 2).
      const ids = this.negeriLookup.length
        ? this.negeriLookup.map(n => n.id)
        : Object.keys(byNg);
      return ids
        .map(id => {
          const r = byNg[id] || {};
          return { ...shapeRow(r, r.negeri || this.negeriName[id] || id), ng: id };
        })
        // fixed kod-negeri order — the table always reads this; the Susunan
        // control re-orders chartRows only, never the table
        .sort((a, b) => (parseInt(a.ng, 10) || 0) - (parseInt(b.ng, 10) || 0));
    },
    // chart/list rendering order per the Susunan control (kod or peratus)
    chartRows() {
      return [...this.allRows].sort(this.rowSorter("ng"));
    },
    // bar-graph view: one row per negeri, height follows the row count
    // 36px setiap negeri, bukan 26: itulah tinggi slot kategori, dan tebal bar
    // ialah pecahan daripadanya (~0.72 selepas bar/categoryPercentage lalai).
    // Menaikkan `maxBarThickness` sahaja tidak menebalkan bar — cap itu hanya
    // HAD ATAS; yang menentukan ialah ruang yang ada dalam slot.
    negeriChartHeight() {
      return Math.max(240, this.allRows.length * 36 + 64);
    },
    // daerah pentadbiran of the selected negeri (respects the DP filter), same
    // shape + sort as the negeri bars — feeds the daerah bar-graph card
    daerahRows() {
      return this.apiDaerah
        .map(d => ({ ...shapeRow(d, d.namaDP || d.dp), dp: d.dp }))
        // same Susunan ordering as the negeri bars (kod = daerah code here)
        .sort(this.rowSorter("dp"));
    },
    daerahChartHeight() {
      return Math.max(200, this.daerahRows.length * 36 + 64);
    },
    // shared filename suffix for the chart downloads: pusingan + sampel + date
    chartFileParts() {
      const parts = [];
      const rounds = this.selectedRounds.length ? this.selectedRounds : this.roundItems;
      if (rounds.length) {
        const lo = rounds[0], hi = rounds[rounds.length - 1];
        parts.push("Pusingan" + (lo === hi ? `${lo}` : `${lo}-${hi}`));
      }
      if (this.sampel === "1" || this.sampel === "2") parts.push("Sampel" + this.sampel);
      parts.push(this.todayStr.replace(/-/g, ""));
      return parts;
    },
    currentRows() {
      let rows = this.allRows;
      if (this.negeri !== "ALL") rows = rows.filter(r => r.ng === this.negeri);
      return rows;
    },
    // the selected-negeri total, DP-scoped when a daerah is chosen (from API scopeTotal)
    scopeRow() {
      if (this.apiScopeTotal) return shapeRow(this.apiScopeTotal, this.scopeName);
      return this.currentRows[0] || null;
    },
    scope() {
      if (this.negeri === "ALL") return this.malaysiaTotal(this.currentRows);
      return this.scopeRow || this.malaysiaTotal([]);
    },
    scopeName() {
      if (this.negeri === "ALL") return "Malaysia";
      const neg = this.negeriName[this.negeri] || this.negeri;
      if (this.daerah.length === 1) return `${neg} — ${this.daerahName[this.daerah[0]] || this.daerah[0]}`;
      if (this.daerah.length > 1) return `${neg} — ${this.daerah.length} daerah`;
      return neg;
    },
    tableRows() {
      return this.currentRows;
    },
    dataTableItems() {
      const items = [];
      const isAll = this.negeri === "ALL";

      if (isAll) {
        items.push({ _isTotal: true, ...this.malaysiaTotal(this.currentRows) });
      } else if (this.scopeRow) {
        // Filtered negeri (DP-aware): the total row, styled like Malaysia, above the breakdown
        items.push({ _isTotal: true, ...this.scopeRow });
      }

      // Strata breakdown (Bandar / Luar Bandar) — from API apiStrata
      if (this.apiStrata.length) {
        items.push({ _isGroup: true, state: "Strata" });
        const byName = {};
        this.apiStrata.forEach(s => (byName[s.strata] = s));
        ["Bandar", "Luar Bandar"].forEach(label => {
          if (byName[label]) items.push({ _isStrata: true, ...shapeRow(byName[label], label) });
        });
        items.push({ _isSpacer: true, state: "__spacer__" });
      }

      // Daerah Pentadbiran — only when a specific negeri is selected
      if (!isAll && this.apiDaerah.length) {
        items.push({ _isGroup: true, state: "Daerah Pentadbiran" });
        this.apiDaerah.forEach(d =>
          items.push({ _isDaerah: true, ...shapeRow(d, d.namaDP || d.dp) })
        );
      }

      // Negeri breakdown — only in the Malaysia (ALL) view, mirrors the
      // Daerah Pentadbiran group header above
      if (isAll && this.currentRows.length) {
        items.push({ _isGroup: true, state: "Negeri" });
        items.push(...this.currentRows);
      }
      return items;
    },
    visibleRows() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.dataTableItems;
      return this.dataTableItems.filter(r => r.state.toLowerCase().includes(q));
    },
    hasTotal() {
      return this.visibleRows.some(r => r._isTotal);
    },
    stateCount() {
      return this.visibleRows.filter(r => !r._isTotal).length;
    },
    summaryCards() {
      // raw value + format fn (not a pre-formatted string) so <animated-number>
      // can tween the value on load / filter changes
      return [
        { label: "Jumlah TK", value: this.scope.jumlah, format: this.fmt, sub: "tempat kediaman", color: "" },
        { label: "TK Selesai", value: this.scope.selesai, format: this.fmt, sub: "tempat kediaman", color: T.graf },
        { label: "Tiada Sambutan", value: this.scope.tiadaSambutan, format: this.fmt, sub: "tempat kediaman", color: COL.red },
        { label: "% Selain TSD & EKS", value: this.scope.pctSelain, format: this.pf, sub: "kadar diselaras", color: COL.amber },
      ];
    },
    sampelLabel() {
      return this.sampel === "3" ? "Semua Sampel" : `Sampel ${this.sampel}`;
    },
    filterSummary() {
      const rd = this.allRounds
        ? "Semua pusingan"
        : "Pusingan " + this.selectedRounds.join(",");
      return `${this.negeri === "ALL" ? "Malaysia" : this.scopeName} • ${rd} • ${this.sampelLabel}`;
    },
    // export filename (no extension), built from the active filters, e.g.
    // Ringkasan_Kadar_Respon_Isi_Rumah_Johor_BatuPahat_Pusingan1_Sampel1_20260618
    // Ringkasan_Kadar_Respon_Isi_Rumah_Pusingan12358_20260618
    // Ringkasan_Kadar_Respon_Isi_Rumah_Kedah_Sampel2_20260618
    exportFileBase() {
      // PascalCase a label and strip anything that isn't a letter/digit
      const slug = s => String(s || "")
        .replace(/[^A-Za-z0-9\s]/g, " ")
        .split(/\s+/)
        .filter(Boolean)
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join("");
      const parts = ["Ringkasan_Kadar_Respon_Isi_Rumah"];
      // Negeri (skip Malaysia/ALL)
      if (this.negeri !== "ALL") {
        parts.push(slug(this.negeriName[this.negeri] || this.negeri));
        // Daerah — only when exactly one is selected (a single readable name)
        if (this.daerah.length === 1) {
          parts.push(slug(this.daerahName[this.daerah[0]] || this.daerah[0]));
        }
      }
      // Pusingan — always include, as a readable range based on the filter:
      // "Pusingan2" for a single round, "Pusingan1-12" for a span (incl. when all
      // active rounds are selected). Falls back to the full available range.
      {
        const rounds = this.selectedRounds.length ? this.selectedRounds : this.roundItems;
        if (rounds.length) {
          const lo = rounds[0];
          const hi = rounds[rounds.length - 1];
          parts.push("Pusingan" + (lo === hi ? `${lo}` : `${lo}-${hi}`));
        }
      }
      // Sampel — omit when "3" (Semua)
      if (this.sampel === "1" || this.sampel === "2") {
        parts.push("Sampel" + this.sampel);
      }
      // Date YYYYMMDD
      parts.push(this.todayStr.replace(/-/g, ""));
      return parts.join("_");
    },
  },
  watch: {
    scope() {
      this.$nextTick(this.drawDonut);
    },
    // negeri card view toggle: (re)draw the bar graph once its canvas exists
    negeriView() {
      this.$nextTick(this.drawNegeriChart);
    },
    // data or sort order changed while the bar graph is showing → redraw it
    chartRows() {
      if (this.negeriView === "chart") this.$nextTick(this.drawNegeriChart);
    },
    // daerah card appears/updates with the selected negeri's data (and sort order)
    daerahRows() {
      this.$nextTick(this.drawDaerahChart);
    },
    negeri() {
      // changing negeri invalidates the daerah selection
      this.daerah = [];
      this.queueFetch();
      // bar-graph view: repaint the selection highlight immediately (the values
      // themselves don't change until the refetch lands)
      if (this.negeriView === "chart") this.$nextTick(this.drawNegeriChart);
    },
    daerah() {
      this.queueFetch();
    },
    // keep From <= To: raising From past To drags To along
    roundFrom(v) {
      if (v != null && this.roundTo != null && this.roundTo < v) this.roundTo = v;
    },
    // selectedRoundsModel is a computed derived from the From–To range; watching it
    // refetches whenever either bound changes (and skips no-op range edits)
    selectedRoundsModel() {
      this.queueFetch();
    },
    sampel() {
      this.queueFetch();
    },
  },
  async mounted() {
    // Restricted roles (PP/PO) are pinned to their own negeri — lock the selector and
    // default it to their kodneg before the first fetch. (Backend enforces this too.)
    const lock = negeriLock(this.$store);
    if (lock.locked) {
      this.negeri = lock.kodneg;
      this.negeriLocked = true;
    }
    // load the active-pusingan filter first (status == 1), then fetch the data
    await this.loadActiveRounds();
    this.fetchData();
    this.drawDonut();
  },
  beforeDestroy() {
    if (this.donutChart) this.donutChart.destroy();
    if (this._negeriChart) this._negeriChart.destroy();
    if (this._daerahChart) this._daerahChart.destroy();
    clearTimeout(this._revealTimer);
    clearTimeout(this._revealDoneTimer);
    clearTimeout(this._fetchTimer);
  },
  methods: {
    /* ---- data ---- */
    // coalesce rapid filter changes (e.g. negeri reset clearing daerah) into one request
    queueFetch() {
      clearTimeout(this._fetchTimer);
      this._fetchTimer = setTimeout(() => this.fetchData(), 0);
    },
    // empty all aggregated data so the donut/bars/KPIs/table read 0
    clearData() {
      this.apiNegeri = [];
      this.apiStrata = [];
      this.apiDaerah = [];
      this.apiScopeTotal = null;
    },
    async fetchData() {
      // no pusingan selected → no scope at all; show zeros instead of "all rounds"
      if (!this.selectedRounds.length) {
        this.clearData();
        this.loading = false;
        return;
      }
      this.loading = true;
      try {
        const params = {
          P: this.selectedRounds.join(","),   // pusingan list (1..12)
          S: this.sampel,                      // "1"/"2" filter by sample, "3" = Semua
        };
        if (this.negeri !== "ALL") params.NG = this.negeri;
        if (this.negeri !== "ALL" && this.daerah.length) params.DP = this.daerah.join(",");
        const res = await axios.get(`${process.env.VUE_APP_API_URL}/JadualAwalan/getKadarResponJR2`, { params });
        const d = (res.data && res.data.data) || {};
        this.apiNegeri = d.perNegeri || [];
        this.apiStrata = d.strata || [];
        this.apiDaerah = d.daerah || [];
        this.apiDaerahList = d.daerahList || [];
        this.apiScopeTotal = d.scopeTotal || null;
        // build/refresh the negeri lookup from perNegeri (id ↔ nama)
        if (this.apiNegeri.length && this.apiNegeri[0].negeri) {
          const seen = {};
          this.apiNegeri.forEach(r => { if (r.ng && !seen[r.ng]) seen[r.ng] = r.negeri; });
          // Overwrite when we got the full list (ALL view) so the dropdown stays complete,
          // OR when the user is negeri-locked (the API returns only their own negeri, so
          // the lookup/chart/table should reflect exactly that single negeri).
          if (this.negeri === "ALL" || this.negeriLocked) {
            this.negeriLookup = Object.keys(seen).sort().map(id => ({ id, nama: seen[id] }));
          }
        }
      } catch (err) {
        this.snackbar = { show: true, message: (err && err.message) || "Gagal memuatkan data.", color: "error", timeout: 4000 };
      }
      this.loading = false;
      // page-enter reveal: bars mount at 0 width, then (once painted) transition
      // to their real value — staggered per bar via the CSS below. Once the
      // sweep has played, drop back to the default quick width transition so
      // later filter changes don't fight the FLIP re-sort animation.
      if (!this.barsRevealed) {
        clearTimeout(this._revealTimer);
        this._revealTimer = setTimeout(() => {
          this.barsRevealed = true;
          this._revealDoneTimer = setTimeout(() => { this.revealPlaying = false; }, 2000);
        }, 250);
      }
    },
    malaysiaTotal(rows) {
      const t = { state: "MALAYSIA", selesai: 0, jumlah: 0 };
      COMP.forEach(k => (t[k] = 0));
      rows.forEach(r => {
        t.selesai += r.selesai;
        t.jumlah += r.jumlah;
        COMP.forEach(k => (t[k] += r[k]));
      });
      t.tsdeks = SELAIN_TSD_EKS.reduce((a, k) => a + t[k], 0);
      t.tiadaSambutan = t.jumlah - t.selesai;
      t.pctSelesai = t.jumlah ? (t.selesai / t.jumlah) * 100 : 0;
      t.pctSelain = t.jumlah ? (t.tsdeks / t.jumlah) * 100 : 0;
      return t;
    },
    /* ---- format ---- */
    fmt(n) {
      return new Intl.NumberFormat("en-MY").format(Math.round(n || 0));
    },
    pf(n) {
      return (n || 0).toFixed(1) + "%";
    },
    // table/export variant: plain number — the "%" unit lives in the column headers
    pfn(n) {
      return (n || 0).toFixed(1);
    },
    pctColor(p) {
      return p >= 90 ? T.graf : p >= 85 ? COL.amber : COL.red;
    },
    // chart-only compare fn per the Susunan control; codeField = "ng" (negeri) or
    // "dp" (daerah). "pct" direction follows pctDesc (re-click the button to flip).
    rowSorter(codeField) {
      if (this.sortBy === "pct") {
        return (a, b) => (this.pctDesc ? b.pctSelesai - a.pctSelesai : a.pctSelesai - b.pctSelesai);
      }
      return (a, b) => (parseInt(a[codeField], 10) || 0) - (parseInt(b[codeField], 10) || 0);
    },
    // flip direction only when Peratus was ALREADY active as the click began —
    // mousedown snapshots the state before Vuetify's toggle updates sortBy
    notePctState() {
      this._pctWasActive = this.sortBy === "pct";
    },
    togglePctDir() {
      if (this._pctWasActive) this.pctDesc = !this.pctDesc;
    },
    rowClass(item) {
      return item._isTotal ? "kr2-total-row" : "";
    },
    /* ---- filters ---- */
    // select the full available range (first–last available pusingan)
    selectFullRange() {
      if (!this.roundItems.length) return;
      this.roundFrom = this.roundItems[0];
      this.roundTo = this.roundItems[this.roundItems.length - 1];
    },
    resetFilters() {
      // Locked roles (PP/PO/FE) stay pinned to their own negeri — "ALL" is not in
      // their negeriList, so resetting to it would blank the select and mislabel
      // the scope (chip/exports would read "Malaysia").
      if (!this.negeriLocked) this.negeri = "ALL";
      this.daerah = [];
      this.sampel = "3";
      this.search = "";
      this.sortBy = "kod";
      this.selectFullRange();
    },
    // Load the active pusingan (status == 1) and default the range to all of them
    // (1–n). Mirrors the Wajaran view's getAllPusingan() + status filter. Falls
    // back to 1..12 if the service is unavailable.
    async loadActiveRounds() {
      let rounds = Array.from({ length: 12 }, (_, i) => i + 1);
      try {
        const res = await pusinganSvc.getAllPusingan();
        const rows = (res && res.data && res.data.data) || [];
        const active = rows
          .filter(r => r.status == "1" || r.status == 1)
          .map(r => parseInt(r.pusingan, 10))
          .filter(n => !isNaN(n));
        const uniq = [...new Set(active)].sort((a, b) => a - b);
        if (uniq.length) rounds = uniq;
      } catch (e) {
        console.error("loadActiveRounds failed:", e);
      }
      this.roundItems = rounds;
      this.selectFullRange();
    },
    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
      this.$nextTick(() => {
        if (this.donutChart) this.donutChart.resize();
      });
    },
    /* ---- negeri bar-graph view (alternative to the progress list) ---- */
    drawNegeriChart() {
      const ctx = this.$refs.negeriChart;
      if (!ctx || this.negeriView !== "chart") {
        // canvas gone (switched back to the progress list) → drop the instance
        if (this._negeriChart) { this._negeriChart.destroy(); this._negeriChart = null; }
        return;
      }
      const rows = this.chartRows;
      // callbacks read the CURRENT rows via this ref, so in-place updates
      // (below) keep tooltips/clicks correct without recreating the chart
      this._negeriChartRows = rows;
      const labels = rows.map(r => r.state);
      const data = rows.map(r => Math.round(r.pctSelesai * 10) / 10);
      // selection highlight: the chosen negeri keeps its full band colour + a navy
      // outline, every other bar fades back (mirrors the list view's teal ring)
      const selNg = this.negeri;
      const isMuted = r => selNg !== "ALL" && r.ng !== selNg;
      const colors = rows.map(r =>
        isMuted(r) ? hexA(this.pctColor(r.pctSelesai), 0.3) : this.pctColor(r.pctSelesai)
      );
      const borderColors = rows.map(r =>
        selNg !== "ALL" && r.ng === selNg ? COL.navy : "rgba(0,0,0,0)"
      );
      const muted = rows.map(isMuted);
      const selIdx = selNg !== "ALL" ? rows.findIndex(r => r.ng === selNg) : -1;
      const selIndex = selIdx >= 0 ? selIdx : null;
      // numeric [selesai, jumlah] pairs — pctLabelPlugin lerps old → new
      const countPairs = rows.map(r => [r.selesai, r.jumlah]);
      // update in place — a refetch with identical values (e.g. the Negeri/Daerah
      // filters, which don't affect this card) causes no visible motion, and real
      // changes (sort, pusingan, sampel) tween smoothly instead of restarting
      if (this._negeriChart) {
        const c = this._negeriChart;
        const ds = c.data.datasets[0];
        ds._prevCounts = ds._counts || countPairs.map(() => [0, 0]);
        c.data.labels = labels;
        Object.assign(ds, {
          data,
          backgroundColor: colors,
          borderColor: borderColors,
          _muted: muted,
          _selIndex: selIndex,
          _counts: countPairs,
        });
        c._labelProgress = 0;
        c.update();
        return;
      }
      const self = this;
      this._negeriChart = new Chart(ctx, {
        type: "horizontalBar",
        data: {
          labels,
          datasets: [{
            data,
            // colour = the same ≥90 / 85–89 / <85 bands as the card legend
            backgroundColor: colors,
            // navy outline on the selected negeri, transparent elsewhere
            borderColor: borderColors,
            borderWidth: 2,
            _muted: muted,
            _selIndex: selIndex,
            // selesai/jumlah on each bar (pctLabelPlugin); entrance counts up from 0
            _counts: countPairs,
            _prevCounts: countPairs.map(() => [0, 0]),
            maxBarThickness: 26,
          }],
        },
        plugins: [pctLabelPlugin],
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: {
            duration: 900,
            easing: "easeOutQuart",
            // expose the tween progress so pctLabelPlugin can roll the counts
            onProgress: a => {
              if (self._negeriChart) {
                self._negeriChart._labelProgress = a.numSteps ? a.currentStep / a.numSteps : 1;
              }
            },
            onComplete: () => {
              if (!self._negeriChart) return;
              self._negeriChart._labelProgress = 1;
              // settle: prev = current, so hover-triggered re-renders (which also
              // restart the animation progress) lerp between equal values → no roll
              const ds = self._negeriChart.data.datasets[0];
              if (ds && ds._counts) ds._prevCounts = ds._counts;
            },
          },
          legend: { display: false },   // single series — the band legend on the card title explains colours
          layout: { padding: { right: 52 } },   // room for the % labels past the bar ends
          tooltips: {
            callbacks: {
              label: t => {
                const r = self._negeriChartRows[t.index];
                return ` ${self.pf(r.pctSelesai)} — ${self.fmt(r.selesai)}/${self.fmt(r.jumlah)} selesai`;
              },
            },
          },
          scales: {
            xAxes: [{ ticks: { min: 0, max: 100, callback: v => v + "%" }, gridLines: { color: "#EEF2F6" } }],
            yAxes: [{ gridLines: { display: false }, ticks: { fontSize: 13, autoSkip: false } }],
          },
          // same behaviour as the progress list: click a bar to scope that negeri
          onClick: (e, els) => {
            if (els && els.length) {
              const r = self._negeriChartRows[els[0]._index];
              self.negeri = self.negeri === r.ng ? "ALL" : r.ng;
            }
          },
        },
      });
      this._negeriChart._labelProgress = 0;
    },
    // composite a chart canvas onto a title + filter-summary band and download it.
    //   'png' → fully transparent background · 'jpg' → white background
    saveChartImage(chart, title, fileBase, format) {
      if (!chart) return;
      const jpg = format === "jpg";
      const src = chart.canvas;
      // Chart.js renders at devicePixelRatio — scale the header band to match
      const scale = src.width / (src.clientWidth || src.width);
      const pad = Math.round(56 * scale);
      const out = document.createElement("canvas");
      out.width = src.width;
      out.height = src.height + pad;
      const c = out.getContext("2d");
      if (jpg) {
        // JPEG has no alpha channel — unfilled pixels would turn black
        c.fillStyle = "#fff";
        c.fillRect(0, 0, out.width, out.height);
      }
      c.fillStyle = COL.navy;
      c.font = `700 ${Math.round(17 * scale)}px Roboto, sans-serif`;
      c.fillText(title, Math.round(16 * scale), Math.round(26 * scale));
      c.fillStyle = "#64748B";
      c.font = `400 ${Math.round(13 * scale)}px Roboto, sans-serif`;
      c.fillText(this.filterSummary, Math.round(16 * scale), Math.round(44 * scale));
      c.drawImage(src, 0, pad);
      const a = document.createElement("a");
      a.href = jpg ? out.toDataURL("image/jpeg", 0.95) : out.toDataURL("image/png");
      a.download = fileBase + (jpg ? ".jpg" : ".png");
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
    downloadNegeriChart(format) {
      this.saveChartImage(
        this._negeriChart,
        "Kadar Respon Mengikut Negeri",
        ["Graf_Kadar_Respon_Mengikut_Negeri", ...this.chartFileParts].join("_"),
        format
      );
    },
    downloadDaerahChart(format) {
      const negName = this.negeriName[this.negeri] || this.negeri;
      this.saveChartImage(
        this._daerahChart,
        `Kadar Respon Mengikut Daerah Pentadbiran — ${negName}`,
        ["Graf_Kadar_Respon_Daerah", negName.replace(/[^A-Za-z0-9]+/g, ""), ...this.chartFileParts].join("_"),
        format
      );
    },
    // daerah pentadbiran bar graph — same style/animation as the negeri chart,
    // created once and updated in place (no restart animation on refetches)
    drawDaerahChart() {
      const ctx = this.$refs.daerahChart;
      if (!ctx || this.negeri === "ALL" || !this.daerahRows.length) {
        if (this._daerahChart) { this._daerahChart.destroy(); this._daerahChart = null; }
        return;
      }
      const rows = this.daerahRows;
      this._daerahChartRows = rows;
      const labels = rows.map(r => r.state);
      const data = rows.map(r => Math.round(r.pctSelesai * 10) / 10);
      const colors = rows.map(r => this.pctColor(r.pctSelesai));
      const countPairs = rows.map(r => [r.selesai, r.jumlah]);
      if (this._daerahChart) {
        const c = this._daerahChart;
        const ds = c.data.datasets[0];
        ds._prevCounts = ds._counts || countPairs.map(() => [0, 0]);
        c.data.labels = labels;
        Object.assign(ds, { data, backgroundColor: colors, _counts: countPairs });
        c._labelProgress = 0;
        c.update();
        return;
      }
      const self = this;
      this._daerahChart = new Chart(ctx, {
        type: "horizontalBar",
        data: {
          labels,
          datasets: [{
            data,
            backgroundColor: colors,
            _counts: countPairs,
            _prevCounts: countPairs.map(() => [0, 0]),
            borderWidth: 0,
            maxBarThickness: 26,
          }],
        },
        plugins: [pctLabelPlugin],
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: {
            duration: 900,
            easing: "easeOutQuart",
            onProgress: a => {
              if (self._daerahChart) {
                self._daerahChart._labelProgress = a.numSteps ? a.currentStep / a.numSteps : 1;
              }
            },
            onComplete: () => {
              if (!self._daerahChart) return;
              self._daerahChart._labelProgress = 1;
              const ds = self._daerahChart.data.datasets[0];
              if (ds && ds._counts) ds._prevCounts = ds._counts;
            },
          },
          legend: { display: false },
          layout: { padding: { right: 52 } },
          tooltips: {
            callbacks: {
              label: t => {
                const r = self._daerahChartRows[t.index];
                return ` ${self.pf(r.pctSelesai)} — ${self.fmt(r.selesai)}/${self.fmt(r.jumlah)} selesai`;
              },
            },
          },
          scales: {
            xAxes: [{ ticks: { min: 0, max: 100, callback: v => v + "%" }, gridLines: { color: "#EEF2F6" } }],
            yAxes: [{ gridLines: { display: false }, ticks: { fontSize: 13, autoSkip: false } }],
          },
        },
      });
      this._daerahChart._labelProgress = 0;
    },
    /* ---- donut ---- */
    drawDonut() {
      const ctx = this.$refs.donut;
      if (!ctx) return;
      const data = [this.scope.selesai, this.scope.tiadaSambutan];
      if (this.donutChart) {
        const prev = this.donutChart.data.datasets[0].data;
        const prevTotal = (+prev[0] || 0) + (+prev[1] || 0);
        if (prevTotal === 0 && data[0] + data[1] > 0) {
          // first real data after the empty page-enter chart: update() from an
          // all-zero chart shows no tween, so recreate to play the rotate-in sweep
          this.donutChart.destroy();
          this.donutChart = null;
        } else {
          // update the existing chart in place (smooth animation, no flash)
          this.donutChart.data.datasets[0].data = data;
          this.donutChart.update();
          return;
        }
      }
      // first render: create the chart once
      this.donutChart = new Chart(ctx, {
        type: "doughnut",
        data: {
          labels: ["Selesai", "Tiada Sambutan"],
          datasets: [{
            data,
            backgroundColor: [T.graf, "#E3E9F0"],
            borderWidth: 0,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutoutPercentage: 74,
          // page-enter/update sweep, paced to match the bars' fill animation
          animation: { duration: 1200, easing: "easeOutQuart", animateRotate: true },
          legend: { display: false },
          tooltips: {
            callbacks: { label: (item, data) => " " + data.labels[item.index] + ": " + this.fmt(data.datasets[0].data[item.index]) },
          },
        },
      });
    },
    /* ---- styled .xlsx export (matches table colours + official header block) ---- */
    async exportExcel() {
      this.exporting = true;
      try {
        await loadTheme();
        await loadScript("/js/exceljs.min.js");
        const ExcelJS = window.ExcelJS;

        // ARGB palette (mirrors the on-screen table)
        const NAVY = argb(T.tajukLaporan), HEAD = argb(T.kepalaJadual), NAVY2 = argb(dark(T.kepalaJadual)), TEAL = argb(T.graf),
              AMBER = "FFE0A800", RED = "FFD9534F", WHITE = "FFFFFFFF",
              ZEBRA = "FFFAFCFE", LINE = "FFE2E8F0";
        const thin = { style: "thin", color: { argb: LINE } };
        const border = { top: thin, left: thin, bottom: thin, right: thin };
        const center = { vertical: "middle", horizontal: "center", wrapText: true };
        const fill = argb => ({ type: "pattern", pattern: "solid", fgColor: { argb } });

        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet("Kadar Respon", { views: [{ showGridLines: false }] });
        const COLW = [20, 14, 14, 12, 16, 18, 12, 12, 11, 14, 14, 14, 12, 16];
        ws.columns = COLW.map(w => ({ width: w }));

        // ── Logo row 1: Jata Negara crest + config-driven title (services/exportHeader) ──
        await addJataHeaderExcel(wb, ws, COLW, "N");

        // ── Title block (rows 2-3, below the logo) ───────────────────────────
        ws.mergeCells("A2:N2");
        Object.assign(ws.getCell("A2"), {
          value: await surveyTitle(),
          alignment: center, font: { bold: true, size: 12, color: { argb: WHITE } }, fill: fill(NAVY),
        });
        ws.getRow(2).height = 24;

        ws.mergeCells("A3:N3");
        Object.assign(ws.getCell("A3"), {
          value: "RINGKASAN KADAR RESPON ISI RUMAH",
          alignment: center, font: { bold: true, size: 11, color: { argb: WHITE } }, fill: fill(TEAL),
        });
        ws.getRow(3).height = 20;

        // ── Filter summary (row 5) ───────────────────────────────────────────
        const negeriLabel = this.negeri === "ALL" ? "Malaysia" : this.scopeName;
        [
          ["A5", "Pilihan :", "B5:D5", negeriLabel],
          ["E5", "Pusingan :", "F5:H5", this.ddLabel],
          ["I5", "Sampel :", "J5:K5", this.sampelLabel],
          ["L5", "Eksport :", "M5:N5", "Excel"],
        ].forEach(([lc, lt, mr, val]) => {
          Object.assign(ws.getCell(lc), { value: lt, font: { bold: true, size: 10 }, alignment: { vertical: "middle", horizontal: "left" } });
          ws.mergeCells(mr);
          Object.assign(ws.getCell(mr.split(":")[0]), { value: val, font: { size: 10 }, alignment: { vertical: "middle", horizontal: "left" } });
        });

        // ── Grouped header (rows 7–8) ────────────────────────────────────────
        const setH = (cell, text, bg) => Object.assign(ws.getCell(cell), {
          value: text, alignment: center, border,
          font: { bold: true, size: 10, color: { argb: WHITE } }, fill: fill(bg || HEAD),
        });
        [
          ["A7:A8", "KATEGORI"], ["B7:B8", "JUMLAH\nTEMPAT\nKEDIAMAN"], ["C7:C8", "TEMPAT KEDIAMAN\nSELESAI\n(1)"],
          ["M7:M8", "%\nSELESAI"], ["N7:N8", "% SELESAI SELAIN\nTSD & EKS"],
        ].forEach(([rng, t]) => { ws.mergeCells(rng); setH(rng.split(":")[0], t); });
        ws.mergeCells("D7:J7");
        setH("D7", "TIADA SAMBUTAN");
        ws.mergeCells("K7:L7");
        setH("K7", "TIADA SAMBUTAN TSD & EKS");
        const sub = [
          "KOSONG\n(2)", "RUMAH DALAM PEMBINAAN\n(4)", "BUKAN TEMPAT KEDIAMAN/ TK INSTITUSI\n(5)",
          "ROBOH/ USANG\n(6)", "TEMPAT LAYAK\n(7)", "LAIN-LAIN\n(9)", "LAIN-LAIN DIDIAMI\n(0)",
          "TIADA SIAPA DIRUMAH\n(3)", "ENGGAN KERJASAMA\n(8)",
        ];
        ["D8", "E8", "F8", "G8", "H8", "I8", "J8", "K8", "L8"].forEach((addr, i) => setH(addr, sub[i], NAVY2));
        ws.getRow(7).height = 46;
        ws.getRow(8).height = 46;

        // ── Data rows (from row 9) ───────────────────────────────────────────
        const GROUP = "FFE8EEF3", SPACER = "FF475569", SUB = "FFF4F8FA";
        let rowIdx = 9;
        this.visibleRows.forEach((r, idx) => {
          const row = ws.getRow(rowIdx);

          // Group label (Strata / Daerah Pentadbiran) → merged band
          if (r._isGroup) {
            ws.mergeCells(`A${rowIdx}:N${rowIdx}`);
            Object.assign(ws.getCell(`A${rowIdx}`), {
              value: (r.state || "").toUpperCase(), border,
              alignment: { vertical: "middle", horizontal: "left" },
              font: { bold: true, size: 10, color: { argb: "FF0B1F3A" } }, fill: fill(GROUP),
            });
            row.height = 18;
            rowIdx++;
            return;
          }
          // Spacer → thin grey divider
          if (r._isSpacer) {
            ws.mergeCells(`A${rowIdx}:N${rowIdx}`);
            Object.assign(ws.getCell(`A${rowIdx}`), { fill: fill(SPACER) });
            row.height = 5;
            rowIdx++;
            return;
          }

          const isTotal = !!r._isTotal;
          const isSub = !!r._isStrata || !!r._isDaerah;
          const vals = [r.state, r.jumlah, r.selesai, r.kosong, r.pembinaan, r.bukan, r.roboh,
            r.layak, r.lain9, r.lain0, r.tiada3, r.enggan8, r.pctSelesai, r.pctSelain];
          vals.forEach((val, ci) => {
            const cell = row.getCell(ci + 1);
            if (typeof val === "number") cell.value = ci >= 12 ? Math.round(val * 10) / 10 : Math.round(val);
            else cell.value = val;
            cell.border = border;
            cell.alignment = ci === 0 ? { vertical: "middle", horizontal: "left", indent: isSub ? 1 : 0 } : { vertical: "middle", horizontal: "right" };
            cell.font = { size: 11, bold: ci === 0 && !isSub };
            if (ci >= 1 && ci <= 11) cell.numFmt = "#,##0";
            if (ci >= 12) cell.numFmt = '0.0';
          });
          if (isTotal) {
            for (let c = 1; c <= 14; c++) {
              const cell = row.getCell(c);
              cell.fill = fill(TEAL);
              cell.font = { size: 11, bold: true, color: { argb: WHITE } };
            }
          } else {
            if (isSub) for (let c = 1; c <= 14; c++) row.getCell(c).fill = fill(SUB);
            else if (idx % 2 === 1) for (let c = 1; c <= 14; c++) row.getCell(c).fill = fill(ZEBRA);
            const pc = r.pctSelesai >= 90 ? TEAL : r.pctSelesai >= 85 ? AMBER : RED;
            const cell = row.getCell(13); // % SELESAI
            cell.fill = fill(pc);
            cell.font = { size: 11, bold: true, color: { argb: WHITE } };
          }
          rowIdx++;
        });

        const buf = await wb.xlsx.writeBuffer();
        const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${this.exportFileBase}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (err) {
        this.snackbar = { show: true, message: err.message || "Eksport Excel gagal.", color: "error", timeout: 4000 };
      }
      this.exporting = false;
    },

    /* ---- styled PDF export (matches table colours + official header block) ---- */
    async exportPDF() {
      this.exportingPdf = true;
      try {
        await loadTheme();
        await loadScript("/js/jspdf.umd.min.js");
        await loadScript("/js/jspdf.plugin.autotable.min.js");
        const { jsPDF } = window.jspdf;

        // RGB palette (mirrors the on-screen table)
        const NAVY = rgb(T.tajukLaporan), HEAD = rgb(T.kepalaJadual), NAVY2 = rgb(dark(T.kepalaJadual)), TEAL = rgb(T.graf),
              AMBER = [224, 168, 0], RED = [217, 83, 79];

        const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
        const pageW = doc.internal.pageSize.getWidth();
        const pageH = doc.internal.pageSize.getHeight();

        // ── Watermark (faded HIES logo) — painted FIRST so the title and table
        //    render on top of it. jsPDF has no z-order: paint order is stacking
        //    order, so "behind" = drawn before. 0.14 keeps the blue/green visible.
        const wm = await loadImage(hiesWatermark, 0.14);
        const wmH = 110; // mm
        const wmW = wm.width * (wmH / wm.height);
        const drawWatermark = () =>
          doc.addImage(wm.dataUrl, "PNG", (pageW - wmW) / 2, (pageH - wmH) / 2, wmW, wmH);
        drawWatermark(); // page 1 — later pages via autoTable's willDrawPage hook

        // ── Logo: Jata Negara crest + config-driven title (services/exportHeader) ──
        await addJataHeaderPdf(doc, pageW);

        // ── Title + subtitle (serif for a formal report look) ──
        doc.setFont("times", "bold");
        doc.setFontSize(13);
        doc.setTextColor(NAVY[0], NAVY[1], NAVY[2]);
        doc.text(await surveyTitle(), pageW / 2, 35, { align: "center" });
        doc.setFontSize(11);
        doc.setTextColor(TEAL[0], TEAL[1], TEAL[2]);
        doc.text("RINGKASAN KADAR RESPON ISI RUMAH", pageW / 2, 41.5, { align: "center" });
        doc.setFont("helvetica", "normal");

        // ── Filter summary ──
        const negeriLabel = this.negeri === "ALL" ? "Malaysia" : this.scopeName;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(90);
        doc.text(
          `Pilihan: ${negeriLabel}     |     Pusingan: ${this.ddLabel}     |     Sampel: ${this.sampelLabel}     |     Dijana: ${new Date().toLocaleString("en-MY")}`,
          14, 48
        );

        // ── Table ──
        const head = [
          [
            { content: "KATEGORI", rowSpan: 2 },
            { content: "JUMLAH TEMPAT KEDIAMAN", rowSpan: 2 },
            { content: "TEMPAT KEDIAMAN SELESAI (1)", rowSpan: 2 },
            { content: "TIADA SAMBUTAN", colSpan: 7 },
            { content: "TIADA SAMBUTAN TSD & EKS", colSpan: 2 },
            { content: "% SELESAI", rowSpan: 2 },
            { content: "% SELESAI SELAIN TSD & EKS", rowSpan: 2 },
          ],
          ["KOSONG (2)", "RUMAH DALAM PEMBINAAN (4)", "BUKAN TK / TK INSTITUSI (5)", "ROBOH / USANG (6)",
           "TEMPAT LAYAK (7)", "LAIN-LAIN (9)", "LAIN-LAIN DIDIAMI (0)", "TIADA SIAPA DIRUMAH (3)", "ENGGAN KERJASAMA (8)"],
        ];
        const GROUP = [232, 238, 243], SPACER = [71, 85, 105];
        // tag each body row with its kind so didParseCell can style it
        const kinds = [];
        const body = this.visibleRows.map(r => {
          if (r._isGroup) {
            kinds.push("group");
            return [{ content: (r.state || "").toUpperCase(), colSpan: 14 }];
          }
          if (r._isSpacer) {
            kinds.push("spacer");
            return [{ content: "", colSpan: 14 }];
          }
          kinds.push(r._isTotal ? "total" : (r._isStrata || r._isDaerah) ? "sub" : "state");
          return [
            r.state,
            this.fmt(r.jumlah), this.fmt(r.selesai),
            this.fmt(r.kosong), this.fmt(r.pembinaan), this.fmt(r.bukan), this.fmt(r.roboh), this.fmt(r.layak),
            this.fmt(r.lain9), this.fmt(r.lain0), this.fmt(r.tiada3), this.fmt(r.enggan8),
            this.pfn(r.pctSelesai), this.pfn(r.pctSelain),
          ];
        });

        doc.autoTable({
          head, body,
          startY: 52,
          // pages the table adds get their watermark painted before their content
          // too (page 1 already has it — drawn above, before the header block)
          willDrawPage: d => { if (d.pageNumber > 1) drawWatermark(); },
          // fillColor:false = TRANSPARENT body cells, so the watermark painted
          // beneath the table shows through (the default theme paints every cell
          // white, which was hiding it). Meaningful fills (totals, % bands,
          // group rows, sub rows) are still applied opaquely in didParseCell.
          theme: "plain",
          styles: { font: "helvetica", fontSize: 6.5, cellPadding: 1.4, halign: "right", lineColor: [226, 232, 240], lineWidth: 0.1, valign: "middle", fillColor: false },
          headStyles: { fillColor: HEAD, textColor: 255, halign: "center", fontSize: 6.2, valign: "middle" },
          columnStyles: { 0: { halign: "left", fontStyle: "bold" } },
          didParseCell: d => {
            if (d.section === "head" && d.row.index === 1) {
              d.cell.styles.fillColor = NAVY2;
            }
            if (d.section === "body") {
              const kind = kinds[d.row.index];
              if (kind === "group") {
                d.cell.styles.fillColor = GROUP;
                d.cell.styles.textColor = [11, 31, 58];
                d.cell.styles.fontStyle = "bold";
                d.cell.styles.halign = "left";
              } else if (kind === "spacer") {
                d.cell.styles.fillColor = SPACER;
                d.cell.styles.cellPadding = 0;
                d.cell.styles.fontSize = 1;
                d.cell.styles.minCellHeight = 1;
              } else if (kind === "total") {
                d.cell.styles.fillColor = TEAL;
                d.cell.styles.textColor = 255;
                d.cell.styles.fontStyle = "bold";
              } else {
                if (kind === "sub") {
                  // no fill — transparent like the plain rows, so the watermark
                  // shows through the Bandar / Luar Bandar / daerah breakdowns too
                  if (d.column.index === 0) d.cell.styles.fontStyle = "normal";
                }
                if (d.column.index === 12) {
                  const p = parseFloat(d.row.raw[12]);
                  d.cell.styles.fillColor = p >= 90 ? TEAL : p >= 85 ? AMBER : RED;
                  d.cell.styles.textColor = 255;
                  d.cell.styles.fontStyle = "bold";
                }
              }
            }
          },
        });

        // page numbers (e.g. "1/10") bottom-right on every page — the watermark
        // is painted per page BEFORE the content (see drawWatermark above)
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(120);
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          doc.text(`${i}/${pageCount}`, pageW - 14, pageH - 6, { align: "right" });
        }

        doc.save(`${this.exportFileBase}.pdf`);
      } catch (err) {
        this.snackbar = { show: true, message: err.message || "Eksport PDF gagal.", color: "error", timeout: 4000 };
      }
      this.exportingPdf = false;
    },
  },
};
</script>

<style scoped>
/* ---------- Bar penapis ----------
   Warna daripada palet yang dikongsi `paparanutama`, sidebar (MenuHeader) dan
   <PageHeader>: teal-700 #037769, wash #eef8f6, hair #dbeeeb. */
.filter-bar {
  background: #f7fcfb;
  border-top: 1px solid #dbeeeb;
  border-bottom: 1px solid #dbeeeb;
}

/* Label penapis — sama gaya dengan `.nav-eyebrow` dalam sidebar. */
.fld__label {
  font-size: var(--fs-3xs);
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #7aa8a2;
  line-height: 1.4;
  margin-bottom: 5px;
  white-space: nowrap;
}

/* Medan outlined: birai teal lembut, menyerlah teal-700 bila fokus. */
.filter-bar ::v-deep .v-input fieldset {
  border-color: #cfeae7;
}
.filter-bar ::v-deep .v-input--is-focused fieldset {
  border-color: #037769 !important;
  border-width: 1px;
}
.filter-bar ::v-deep .v-select__selection--comma {
  color: #21403c;
}
.filter-bar ::v-deep .v-input__append-inner .v-icon {
  color: #7aa8a2;
}

/* Butang "Tetapkan Semula" — dulu kelabu neutral; kini senada teal. */
.reset-btn.v-btn--outlined {
  border-color: #cfeae7;
  color: #037769;
  letter-spacing: normal;
}
.reset-btn.v-btn--outlined:hover {
  background: #eef8f6;
  border-color: #037769;
}

.pusingan-dash {
  font-size: var(--fs-xs);
  color: #7aa8a2;
}

/* Compact Pusingan From–To range: two narrow number selects on one line */
.pusingan-range .pusingan-sel {
  width: 84px;
  flex: 0 0 84px;
}
.pusingan-range ::v-deep .v-select__selections {
  flex-wrap: nowrap;
}
.pusingan-range ::v-deep .v-select__selection {
  overflow: visible;
  white-space: nowrap;
}
.pusingan-range ::v-deep .v-input__slot {
  padding: 0 8px !important;
}
.pusingan-range ::v-deep .v-input__append-inner {
  margin-left: 0;
}

.kadar-respon2 {
  padding: 16px;
  /* break out of the centered v-container so the wide table has full-width room */
  margin-left: -12px;
  margin-right: -12px;
}

/* Toggle Sampel: birai sepadan dengan medan outlined di sebelahnya; pilihan
   aktif = teal pejal (dulu biru `primary` Vuetify). */
.data-toggle {
  border: 1px solid #cfeae7;
  border-radius: 4px;
  overflow: hidden;
  background: #ffffff;
}
.data-toggle ::v-deep .v-btn {
  border: none !important;
  letter-spacing: normal;
  color: #4a8a84;
  background: transparent;
}
.data-toggle ::v-deep .v-btn.v-btn--active {
  background: #037769 !important;
  color: #ffffff !important;
}
/* Vuetify meletakkan lapisan legap pada butang aktif — ia mengaburkan teal. */
.data-toggle ::v-deep .v-btn.v-btn--active .v-btn__overlay,
.data-toggle ::v-deep .v-btn.v-btn--active::before {
  opacity: 0 !important;
}

/* Donut area vs KPI area: both flex from a zero basis; the donut's larger
   grow gives it ~60% of the space under the title, KPI tiles get the rest. */
.chart-container {
  position: relative;
  flex: 1.6 1 0;
  min-height: 260px;
  width: 100%;
  container-type: size;
}

.donut-center {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}
.donut-pct { font-size: var(--fs-3xl); font-weight: 800; line-height: 1; }
.donut-lab { font-size: var(--fs-3xs); font-weight: 600; text-transform: uppercase; letter-spacing: .6px; margin-top: 3px; }
/* scale the centre label with the donut (container-type on .chart-container) */
.chart-container .donut-pct { font-size: clamp(26px, 13cqh, 46px); }
.chart-container .donut-lab { font-size: clamp(9px, 4cqh, 13px); }

.kpi-tile { background: #F8FAFC; border-radius: 10px; padding: 9px 10px; text-align: center; }
.kpi-v { font-size: var(--fs-base); font-weight: 700; }
.kpi-l { font-size: var(--fs-3xs); font-weight: 500; text-transform: uppercase; letter-spacing: .4px; margin-top: 2px; }

/* KPI area tracks the card size: the wrapper is a size container, the 2×2 grid
   of tiles fills whatever height is left under the donut, and the type scales
   with the container's height/width (cqh/cqw), clamped so it never gets absurd.
   Browsers without container-query support keep the fixed sizes above. */
.kpi-wrap { container-type: size; min-height: 150px; flex: 1 1 0; }
.kpi-wrap .kpi-row { height: 100%; }
.kpi-wrap .kpi-v { font-size: clamp(15px, min(14cqh, 7cqw), 32px); }
.kpi-wrap .kpi-l { font-size: clamp(9px, min(6cqh, 3cqw), 13px); }

/* Legenda band warna (≥90% / 85–89% / <85%). Dahulu memakai kelas `caption`
   Vuetify (12px) — tetapi legenda ialah KUNCI untuk membaca carta, bukan nota
   kaki: tanpanya warna bar tidak bermakna apa-apa. Diletak pada saiz badan
   jadual supaya ia terbaca sepintas lalu. */
.legend { display: flex; gap: 14px; font-size: var(--fs-sm); }

/* Nota penerangan skop di bawah carta — menerangkan penapis MANA yang memberi
   kesan pada angka di atasnya. Salah faham baris ini bermakna salah tafsir
   carta, jadi ia disamakan dengan legenda dan bukan dibiarkan pada 12px. */
.nota-skop { font-size: var(--fs-sm); line-height: 1.5; }

/* Tiga kawalan di kepala kad graf (Susunan | Paparan | Muat turun) memakai
   birai teal yang sama seperti bar penapis di atasnya — dulu kelabu neutral,
   yang bertentangan dengan bar penapis teal betul-betul di atasnya. */
/* negeri card view toggle (progress list ↔ bar graph) */
.view-toggle { border: 1px solid #cfeae7; border-radius: 4px; overflow: hidden; }
.view-toggle ::v-deep .v-btn { border: none !important; color: #5d8f8a; }
.view-toggle ::v-deep .v-btn--active { background: #e6f6f5 !important; }
.view-toggle ::v-deep .v-btn--active .v-icon { color: var(--warna-graf-gelap, #0B827F) !important; }

/* Susunan toggle in the chart card headers — matches the view toggle look;
   the active option gets a teal tint so the current order is obvious */
.sort-toggle { border: 1px solid #cfeae7; border-radius: 4px; overflow: hidden; }
.sort-toggle ::v-deep .v-btn { border: none !important; letter-spacing: normal; }
.sort-toggle ::v-deep .v-btn--active { background: #e6f6f5 !important; color: var(--warna-graf-gelap, #0B827F) !important; }
.sort-toggle ::v-deep .v-btn--active .v-icon { color: var(--warna-graf-gelap, #0B827F) !important; }

/* Butang muat turun graf — dulu butang ikon bulat tanpa birai, jadi ia
   terapung di sebelah dua kumpulan toggle berbirai. Birai + radius yang sama
   menjadikan ketiga-tiganya satu baris kawalan. */
/* Butang ikon saiz lalai = 36px, sama tinggi dengan butang toggle saiz lalai
   di sebelahnya — jadi baris kawalan itu kekal rata tanpa tinggi tetap. */
.dl-btn.v-btn--icon {
  border: 1px solid #cfeae7;
  border-radius: 4px;
}
/* Ikon dikecilkan daripada 24px lalai: butang ini berdiri di sebelah toggle
   berteks, jadi ikon setinggi teks (18px) membuatnya membaca sebagai kawalan
   yang sama beratnya, bukan butang yang lebih besar. */
.dl-btn ::v-deep .v-icon {
  color: #037769;
  font-size: var(--fs-lg);
  height: var(--fs-lg);
  width: var(--fs-lg);
}
.dl-btn:hover { background: #e6f6f5; border-color: #037769; }
/* Vuetify 2 melukis lapisan kelabu melalui `::before` (BUKAN `.v-btn__overlay`
   — itu Vuetify 3 dan tiada di sini). Tanpa ini, tuding teal jadi kelabu. */
.dl-btn ::v-deep::before { opacity: 0 !important; }
.legend .dot { display: inline-block; width: 9px; height: 9px; border-radius: 50%; margin-right: 5px; vertical-align: middle; }

.sbar { cursor: pointer; border-radius: 8px; padding: 4px 8px; transition: background .15s; }
/* page-enter reveal only: bar fill sweeps from 0 to its value, each bar a beat
   after the one above. Dropped once played — on filter changes the slow tween +
   per-bar delay would fight the FLIP re-sort slide (glitchy), so widths fall
   back to Vuetify's default quick transition there. */
.sbar-cols.revealing .sbar ::v-deep .v-progress-linear__determinate {
  transition: width 1.1s cubic-bezier(.22, 1, .36, 1);
  transition-delay: calc(var(--i, 0) * 35ms);
}
/* counts inside the progress bar (v-progress-linear content slot). White with a
   soft shadow so it reads on the coloured fill; the shadow keeps it legible on
   the light grey track when a bar is short. */
/* Nama negeri + peratusnya dalam senarai bar. Ini NILAI DATA, bukan kapsyen —
   ia setara dengan sel jadual, jadi ia memakai saiz badan jadual. */
.sbar-lab { font-size: var(--fs-sm); }
.sbar-count { font-size: var(--fs-3xs); font-weight: 500; color: #fff; line-height: 1; text-shadow: 0 1px 2px rgba(0, 0, 0, 0.45); }
.sbar:hover { background: #f8fafc; }
.sbar.sel { background: #e6f6f5; box-shadow: inset 0 0 0 1.5px var(--warna-graf, #0EA5A4); }

/* two column-major columns: bars fill top-to-bottom (left 1..n/2, right rest).
   grid gives each bar a stable coordinate so the FLIP move animates cleanly. */
.sbar-cols {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: repeat(var(--bar-rows, 8), auto);
  grid-auto-flow: column;
  column-gap: 16px;
  row-gap: 0px;
  align-content: start;
}
@media (max-width: 599px) {
  .sbar-cols { grid-template-columns: 1fr; grid-template-rows: none; grid-auto-flow: row; }
}
/* GPU-promote bars only while they're actually animating — leaving will-change/
   backface-visibility on permanently makes text and bar edges render blurry */
.sbar-cols .sbar-flip-move,
.sbar-cols .sbar-flip-enter-active,
.sbar-cols .sbar-flip-leave-active { will-change: transform, opacity; }
/* re-sorting (toggle/filter): staggered FLIP move — each bar starts a beat after
   the previous (transition-delay scales with its rank index), so they slide into
   their new order one by one instead of all at once. */
.sbar-flip-move {
  transition: transform 1.4s cubic-bezier(.22, 1, .36, 1);
  transition-delay: calc(var(--i, 0) * 35ms);
}
/* polished scale + fade for bars entering/leaving (initial render, data change) */
.sbar-flip-enter-active { transition: all .8s cubic-bezier(.22, 1, .36, 1); }
.sbar-flip-leave-active { transition: all .35s ease; position: absolute; width: calc(50% - 8px); }
.sbar-flip-enter, .sbar-flip-leave-to { opacity: 0; transform: scale(.96) translateY(6px); }

/* grouped-header detailed table */
::v-deep .kr2-table > .v-data-table__wrapper { overflow-x: auto; }
::v-deep .kr2-table table { border-collapse: collapse; width: 100%; font-size: var(--fs-sm); white-space: nowrap; min-width: 1180px; }
::v-deep .kr2-table thead th {
  background: var(--warna-kepala-jadual, #1B7E91); color: #fff !important; font-weight: 600; padding: 11px 12px;
  text-align: center !important; border: 1px solid #2a8ea1; vertical-align: middle;
  font-size: var(--fs-sm) !important; line-height: 1.3; height: auto;
}
::v-deep .kr2-table thead tr:nth-child(2) th { background: var(--warna-kepala-jadual-2, #15606F); }
::v-deep .kr2-table thead th.cat { position: sticky; left: 0; z-index: 5; }
::v-deep .kr2-table tbody td {
  padding: 10px 12px; border: 1px solid #E2E8F0; text-align: right; color: #0B1F3A; height: auto; font-size: var(--fs-sm);
}
::v-deep .kr2-table tbody td.cat {
  text-align: left; font-weight: 600; position: sticky; left: 0; background: #fff; z-index: 2;
}
::v-deep .kr2-table tbody tr:nth-child(even) td { background: #FAFCFE; }
::v-deep .kr2-table tbody tr:nth-child(even) td.cat { background: #FAFCFE; }
::v-deep .kr2-table tbody tr:hover td { background: #EFF7F7 !important; }
::v-deep .kr2-table tbody tr.total td { background: var(--warna-graf, #0EA5A4) !important; color: #fff; font-weight: 700; border-color: var(--warna-graf-gelap, #0B827F); }
::v-deep .kr2-table tbody tr.group td.cat {
  background: #E8EEF3 !important; color: #0B1F3A; font-weight: 700; text-transform: uppercase;
  letter-spacing: .4px; font-size: var(--fs-xs); text-align: left; position: static;
}
::v-deep .kr2-table tbody tr.strata td { background: #F4F8FA !important; }
::v-deep .kr2-table tbody tr.strata td.cat { background: #F4F8FA !important; padding-left: 24px; font-weight: 600; }
::v-deep .kr2-table tbody tr.spacer td { background: #475569 !important; padding: 0; height: 6px; border: none; }
::v-deep .kr2-table tbody tr.daerah td.cat { padding-left: 24px; font-weight: 500; }
.kr2-table .pill {
  display: inline-block; min-width: 56px; padding: 3px 10px; border-radius: 20px;
  font-size: var(--fs-xs); font-weight: 700; color: #fff;
}

.fullscreen-card {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  z-index: 9999 !important;
  display: flex !important;
  flex-direction: column !important;
  background-color: white !important;
  border-radius: 0 !important;
}
.fullscreen-card .chart-container {
  flex-grow: 1 !important;
  height: auto !important;
}
</style>
